/**
 * @props fees - (object): Object containing billing values
 *      example: {
            "OnlySeparateStatefiling": "false",
            "TotalFilingFee": "0.65",
            "FilingFee": "0.65",
            "USPSFee": "0",
            "TINMatchFee": "0",
            "StateFee": "0",
            "TINCount": "0",
            "StateFilingCount": "0",
            "USPSCount": "0",
            "FormCount": "1",
            "EstimatedTax": "0",
            "eDeliveryFee": "0",
        }

 * @props formName: Name of the form to be displayed while billing
 * @props calculateEstimatedTax: pass it as true, if sales tax needs to be calculate by filling address information
 * @props defaultStep: any one of the value of PAYMENT_STEPS constant
 * @props onPaymentSuccessCb: when the payment is successfull, this function will get called. We can do the operations like fetching the updated list
 * @props paymentServiceFunctionCb: pass the service function for doing payment
                                    Example: PaymentDataService.submitPayment
 * @props paymentServiceFunctionExtraParams: paymentServiceFunctionCb will reveive all the necessary values, but if we need some extra values then pass the key value pairs in this object
 * @props showOnSuccessNavigationBtns: Send this as true along with onSuccessNavigationBtnsArr prop if you want to display navigation buttons on payment success.
 * @props onSuccessNavigationBtnsArr: Array of buttons object that will be displayed on final step on payment success.
 *        example: [
                        {
                            label: "Go to dashboard",
                            type: BUTTON_TYPE.PRIMARY,
                            customStyle: {},
                            onClickCb: () => navigate(`/`),
                        },
                    ]
 */

import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { componentKey, setBillingDetails, setCurrentPaymentStep, setEstimatedTaxCalculationComplete, setIsSubscription, setIsUSPS, setPaymentAddressDetails, setPaymentDrawerShowOnSuccessNavigationBtnsState } from './PaymentDrawerSlice'
import { componentKey as addressInformationKey } from './addressinformationcard/AddressInformationCardSlice.js'
import useGarbageCollector from '../../../../libs/garbagecollector/garbageCollectorHooks'
import { PAYMENT_DRAWER_LOADING_STATES, PAYMENT_STEPS, PLACE_OF_ORIGIN_FOR_PAYMENT } from './constants'
import BillDetails from './billdetails/BillDetails'
import { PaymentSuccess } from './paymentsuccess/PaymentSuccess'
import Loadable from '../../../components/loadable/Loadable'
import { getPrepayAmount, getPrepaySalesTax, getSavedAddress, paynow } from './PaymentDrawerSaga'
import AddFunds from './addfunds/AddFunds'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import Config from "../../../config.web.js";
import DrawerWithTitleAndFooter from '../../../components/drawer/DrawerWithTitleAndFooter'
import commonStyles from "./PaymentDrawer.styles"
import { PAGE_STATE } from '../../constants/constants'
import General from '../../../../libs/utility/General'
import useTxNavigate from '../../../../libs/txnavigate/TxNavigateHook.js'

export default function PaymentDrawer({
    isOpen = false,
    onCloseCb = () => { },
    fees,
    calculateEstimatedTax = true,
    calculatePrepaySalesTax = false,
    placeOfOriginforpayment = PLACE_OF_ORIGIN_FOR_PAYMENT.SUBMIT_FORM_PAGE,
    defaultStep = PAYMENT_STEPS.BILL_DETAILS,
    paymentServiceFunctionCb = () => { },
    onPaymentSuccessCb = () => { },
    paymentServiceFunctionExtraParams = {},
    filingFeeLabel = "Filing Fee",
    isUsps = false,
    isSubscription = false,
    showOnSuccessNavigationBtns = false,
    onSuccessNavigationBtnsArr = [],
}) {

    useGarbageCollector(componentKey);
    useGarbageCollector(addressInformationKey);

    const [stripePromise, setStripePromise] = useState(() => loadStripe(Config.STRIPE_PUBLISHABLE_KEY))

    const dispatch = useDispatch();
    const navigate = useTxNavigate();

    const { loadingState, billingDetails, currentPaymentStep, paymentSuccessDetails, paymentAddressDetails, isCardPayment, saveAddress, paymentDrawerShowOnSuccessNavigationBtnsState, isPrepaidAndCardTransaction } = useSelector(state => state[componentKey]);

    useEffect(() => {
        if (!calculateEstimatedTax) {
            dispatch(setEstimatedTaxCalculationComplete(true))
        }

        if (calculatePrepaySalesTax) {
            dispatch(getPrepaySalesTax({ totalAmount: fees?.TotalFilingFee, isUsps: false }))
        }

        dispatch(setCurrentPaymentStep(defaultStep))
        dispatch(getPrepayAmount());
        dispatch(getSavedAddress());

        dispatch(setIsUSPS(isUsps))
        dispatch(setIsSubscription(isSubscription))

    }, [])

    useEffect(() => {
        dispatch(setPaymentDrawerShowOnSuccessNavigationBtnsState(showOnSuccessNavigationBtns));
    }, [showOnSuccessNavigationBtns])

    useEffect(() => {
        if (fees?.TotalFilingFee !== undefined) {
            dispatch(setBillingDetails({ ...fees, FinalFilingFee: Number(fees?.TotalFilingFee) }))
        }
    }, [fees]);

    const onChangeSalesTax = (salesTax, addressInformation) => {
        dispatch(setPaymentAddressDetails(addressInformation));
        dispatch(setBillingDetails({ ...addressInformation, EstimatedTax: salesTax }));
        dispatch(setEstimatedTaxCalculationComplete(true))
    }

    const drawerTitle = useMemo(() => {
        switch (currentPaymentStep) {
            case PAYMENT_STEPS.BILL_DETAILS:
                return "Here are your billing details";

            case PAYMENT_STEPS.PAYMENT_SUCESS:
                return "";

            case PAYMENT_STEPS.ADD_FUNDS:
                return isCardPayment ? "Pay the bill using card" : "Add funds to Your Prepay wallet";
        }
    }, [currentPaymentStep, isCardPayment]);

    const renderPaymentDrawers = () => {
        switch (currentPaymentStep) {
            case PAYMENT_STEPS.BILL_DETAILS:
                return <BillDetails
                    placeOfOriginforpayment={placeOfOriginforpayment}
                    calculateEstimatedTax={calculateEstimatedTax}
                    onChangeSalesTax={onChangeSalesTax}
                    onPayNowCb={() => {
                        const addressDetails = General.transformToPaymentAddress(paymentAddressDetails)

                        dispatch(paynow({ billingDetails, paymentServiceFunctionCb, onPaymentSuccessCb, paymentServiceFunctionExtraParams, placeOfOriginforpayment, addressDetails, isAddressSave: saveAddress }))
                    }}
                    filingFeeLabel={placeOfOriginforpayment === PLACE_OF_ORIGIN_FOR_PAYMENT.SUBMIT_FORM_PAGE ? (fees?.FormCount ? `${filingFeeLabel} (${fees?.FormCount})` : filingFeeLabel) : filingFeeLabel}
                />

            case PAYMENT_STEPS.PAYMENT_SUCESS:
                return <PaymentSuccess
                    paymentSuccessDetails={paymentSuccessDetails}
                    calculateEstimatedTax={calculateEstimatedTax}
                    successNavigationBtns={paymentDrawerShowOnSuccessNavigationBtnsState}
                    onSuccessNavigationBtnsArr={onSuccessNavigationBtnsArr}
                />

            case PAYMENT_STEPS.ADD_FUNDS:
                return <AddFunds
                    onChangeSalesTax={onChangeSalesTax}
                    placeOfOriginforpayment={placeOfOriginforpayment}
                    onPayNowCb={(cardDetails) => dispatch(paynow({ billingDetails, paymentServiceFunctionCb, onPaymentSuccessCb, paymentServiceFunctionExtraParams: { ...paymentServiceFunctionExtraParams, IsPrepaidAndCardTransaction: isPrepaidAndCardTransaction }, ...cardDetails, isAddressSave: saveAddress }))}
                />
        }
    }

    const { CALCULATE_PREPAY_SALES_TAX_LOADING, GET_PREPAY_AMOUNT_LOADING } = PAYMENT_DRAWER_LOADING_STATES;

    return (
        <>
            <DrawerWithTitleAndFooter
                isOpen={isOpen}
                onCloseCb={onCloseCb}
                title={drawerTitle}
                customStylesDrawer={commonStyles.drawerBody}
                drawerBodyContainerStyles={commonStyles.drawerBodyContainerStyles}
                drawerBodyStyles={commonStyles.drawerBodyStyles}
            >
                <Elements stripe={stripePromise}>
                    <Loadable
                        currentState={loadingState.state}
                        loadingStates={[CALCULATE_PREPAY_SALES_TAX_LOADING, GET_PREPAY_AMOUNT_LOADING, PAGE_STATE.LOADING, PAGE_STATE.PRE_LOADING]}
                        isNewLoadable={true}
                        preserveChildMount={true}
                    >
                        {renderPaymentDrawers()}
                    </Loadable>
                </Elements>
            </DrawerWithTitleAndFooter>
        </>
    )
}
